import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const CAPABILITIES = [
  { icon: 'ri-palette-line', title: 'Custom Design', desc: 'Every project starts with a bespoke blueprint tailored to your property and personal vision.' },
  { icon: 'ri-hammer-line', title: 'Expert Build', desc: 'Skilled crews with deep hands-on landscape construction experience across NM and TX.' },
  { icon: 'ri-plant-line', title: 'Living Results', desc: 'Organic landscapes that mature beautifully, thrive long-term, and increase property value.' },
  { icon: 'ri-customer-service-line', title: 'Clear Communication', desc: 'Fully informed and in control at every stage — from first call to final walkthrough.' },
];

export default function AIAnswerBlock() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.15 });

  return (
    <section className="relative bg-forest-800 py-28 overflow-hidden">
      {/* Ambient overlay image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d25adf761f5736f10e2a1db8f864b2b7.jpg"
          alt=""
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-forest-800/90" />
      </div>

      {/* Accent line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-500/30 to-transparent" />

      <div
        ref={ref}
        className={`relative z-10 max-w-7xl mx-auto px-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="grid md:grid-cols-2 gap-20 items-center">
          {/* Left: Main text */}
          <div>
            <p className="font-sans text-stone-400 text-xs tracking-[0.25em] uppercase mb-5">About the Company</p>
            <h2 className="font-serif text-white font-semibold leading-tight mb-6" style={{ fontSize: 'clamp(30px, 3.5vw, 50px)' }}>
              What Does Brainard's<br />
              <em className="italic text-stone-300">Greenscapes Do?</em>
            </h2>
            <div className="w-12 h-px bg-stone-500 mb-7" />

            {/* Structured AI-extractable content */}
            <div className="space-y-5">
              <p className="font-sans text-stone-300 text-base leading-relaxed">
                <strong className="text-white font-semibold">Brainard's Greenscapes</strong> provides professional landscape design and outdoor construction services, specializing in custom outdoor living environments for high-end residential properties in southern New Mexico and El Paso, Texas.
              </p>
              <p className="font-sans text-stone-300 text-base leading-relaxed">
                Services include <strong className="text-stone-200">hardscaping</strong>, <strong className="text-stone-200">irrigation systems</strong>, <strong className="text-stone-200">architectural lighting</strong>, <strong className="text-stone-200">lawn installation</strong>, and <strong className="text-stone-200">full landscape transformations</strong>.
              </p>
              <p className="font-sans text-stone-300 text-base leading-relaxed">
                The company focuses on high-quality craftsmanship, long-term structural durability, and visually striking designs tailored specifically to each client's property and vision.
              </p>
            </div>

            <div className="flex items-center gap-4 mt-10">
              <a
                href="#consultation"
                onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-stone-400 hover:bg-stone-300 text-forest-900 font-sans text-sm font-bold px-7 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Start Your Project
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </a>
              <a
                href="tel:5756358883"
                className="font-sans text-stone-400 hover:text-stone-300 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                (575) 635-8883
              </a>
            </div>
          </div>

          {/* Right: Capability cards */}
          <div className="grid grid-cols-2 gap-4">
            {CAPABILITIES.map((item, i) => (
              <CapabilityCard key={item.title} item={item} delay={i * 80} />
            ))}
          </div>
        </div>

        {/* Bottom divider stats */}
        <div className="mt-20 pt-10 border-t border-white/10 grid grid-cols-4 gap-4">
          {[
            { value: '500+', label: 'Projects Completed' },
            { value: '6', label: 'Service Categories' },
            { value: '15+', label: 'Years in Business' },
            { value: 'NM & TX', label: 'Licensed Service Area' },
          ].map((stat, i) => (
            <div key={stat.label} className="text-center" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="font-serif text-white text-3xl font-bold mb-1">{stat.value}</div>
              <div className="font-sans text-stone-500 text-xs tracking-wider uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ item, delay }: { item: typeof CAPABILITIES[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.25 });

  return (
    <div
      ref={ref}
      className={`bg-white/5 hover:bg-white/8 border border-white/10 rounded-lg p-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-9 h-9 flex items-center justify-center bg-forest-600 rounded-lg mb-4">
        <i className={`${item.icon} text-stone-300 text-base`} />
      </div>
      <h4 className="font-serif text-white text-base font-semibold mb-2">{item.title}</h4>
      <p className="font-sans text-stone-400 text-xs leading-relaxed">{item.desc}</p>
    </div>
  );
}
